import {
  useEffect,
  useMemo,
  useState
} from "react";

import {
  motion,
  AnimatePresence
} from "framer-motion";
import LuxuryImage from "./LuxuryImage";

import {
  useNavigate
} from "react-router-dom";

import {
  FaSearch,
  FaTimes
} from "react-icons/fa";

function SearchOverlay({
  open,
  onClose,
  products
}) {

  const navigate = useNavigate();

  const [query, setQuery] =
    useState("");

  const [recentSearches, setRecentSearches] =
    useState([]);

  /* ===============================
     RECENT SEARCHES
  =============================== */

  useEffect(() => {

    const stored =
      JSON.parse(
        localStorage.getItem(
          "recentSearches"
        )
      ) || [];

    setRecentSearches(stored);

  }, [open]);

  /* ===============================
     FILTERED PRODUCTS
  =============================== */

  const filteredProducts =
    useMemo(() => {

      if (!query.trim())
        return [];

      return products
        .filter((p) =>
          p.name
            ?.toLowerCase()
            .includes(
              query.toLowerCase()
            )
        )
        .slice(0, 8);

    }, [query, products]);

  /* ===============================
     HANDLE SEARCH CLICK
  =============================== */

  const handleProductClick =
    (product) => {

      let recent =
        JSON.parse(
          localStorage.getItem(
            "recentSearches"
          )
        ) || [];

      recent = recent.filter(
        (r) => r !== query
      );

      recent.unshift(query);

      if (recent.length > 5)
        recent.pop();

      localStorage.setItem(
        "recentSearches",
        JSON.stringify(recent)
      );

      navigate(
        `/product/${product.id}`
      );

      onClose();

      setQuery("");

    };

  const trending = [
    "Glass Art",
    "Wall Decor",
    "Krishna Painting",
    "Luxury Frames"
  ];

  return (

    <AnimatePresence>

      {open && (

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-white/95 backdrop-blur-xl overflow-y-auto"
        >

          <div className="max-w-4xl mx-auto px-6 py-10">

            {/* HEADER */}

            <div className="flex items-center justify-between mb-10">

              <h2 className="text-3xl font-serif italic">
                Search Collection
              </h2>

              <button
                onClick={onClose}
                className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center"
              >
                <FaTimes />
              </button>

            </div>

            {/* SEARCH BAR */}

            <div className="flex items-center bg-stone-100 rounded-full px-6 py-4 mb-10">

              <FaSearch className="text-stone-400 mr-4" />

              <input
                autoFocus
                value={query}
                onChange={(e) =>
                  setQuery(e.target.value)
                }
                placeholder="Search sacred art..."
                className="bg-transparent outline-none w-full text-lg"
              />

            </div>

            {/* TRENDING */}

            {!query && (

              <div className="mb-12">

                <h3 className="text-xs uppercase tracking-[0.3em] text-stone-400 mb-5">

                  Trending Searches

                </h3>

                <div className="flex flex-wrap gap-3">

                  {trending.map((item) => (

                    <button
                      key={item}
                      onClick={() =>
                        setQuery(item)
                      }
                      className="px-5 py-3 rounded-full bg-stone-100 hover:bg-black hover:text-white transition text-sm"
                    >
                      {item}
                    </button>

                  ))}

                </div>

              </div>

            )}

            {/* RECENT */}

            {!query &&
              recentSearches.length > 0 && (

              <div className="mb-12">

                <h3 className="text-xs uppercase tracking-[0.3em] text-stone-400 mb-5">

                  Recent Searches

                </h3>

                <div className="flex flex-wrap gap-3">

                  {recentSearches.map(
                    (item, index) => (

                    <button
                      key={index}
                      onClick={() =>
                        setQuery(item)
                      }
                      className="px-5 py-3 rounded-full border border-stone-200 hover:border-black transition text-sm"
                    >
                      {item}
                    </button>

                  ))}

                </div>

              </div>

            )}

            {/* RESULTS */}

            {query && (

              <div>

                <h3 className="text-xs uppercase tracking-[0.3em] text-stone-400 mb-5">

                  Search Results

                </h3>

                {filteredProducts.length > 0 ? (

                  <div className="grid md:grid-cols-2 gap-5">

                    {filteredProducts.map(
                      (product) => (

                      <motion.div
                        whileHover={{
                          y: -4
                        }}
                        key={product.id}
                        onClick={() =>
                          handleProductClick(product)
                        }
                        className="flex gap-5 p-4 rounded-[2rem] hover:bg-stone-50 cursor-pointer transition"
                      >

                        <LuxuryImage
                          src={
                            product.images?.[0]?.imageUrl ||
                            "/placeholder.png"
                          }
                          alt={product.name}
                          className="
                            w-28 h-28 object-cover
                            rounded-2xl
                          "
                        />

                        <div className="flex flex-col justify-center">

                          <h4 className="font-medium text-lg">
                            {product.name}
                          </h4>

                          <p className="text-stone-500 text-sm mt-1">
                            {product.collectionName}
                          </p>

                          <p className="mt-3 font-semibold">
                            ₹{product.price}
                          </p>

                        </div>

                      </motion.div>

                    ))}

                  </div>

                ) : (

                  <div className="text-center py-20 text-stone-400">

                    No products found ✨

                  </div>

                )}

              </div>

            )}

          </div>

        </motion.div>

      )}

    </AnimatePresence>

  );

}

export default SearchOverlay;